import { Icon } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { Box, Button, Modal, Typography } from '@mui/material';
import { memo, useState } from 'react';
import { useCreditRateCategories } from './hooks/use-credit-rate-categories';
import * as Styled from './styles';
import * as SX from './sx';
import type { CreditUsageOverviewProps as Props } from './types';

export const CreditUsageOverview = memo(({ className }: Props) => {
  const { categories } = useCreditRateCategories();
  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <Styled.Wrapper
      className={classnames('CreditUsageOverview', className)}
      sx={SX.root}
    >
      <Box sx={SX.leftColumn}>
        <Box sx={SX.leftTitleSection}>
          <Typography variant="h5" fontWeight={700} color="common.white" noWrap>
            How Credits Work?
          </Typography>
          <Typography
            variant="body4"
            fontWeight={500}
            color="grey.400"
            textAlign="center"
            noWrap
          >
            Credits let you perform actions on our platform, with each task
            <br />
            costing a different amount based on complexity.
          </Typography>
        </Box>

        <Box sx={SX.videoWrapper}>
          <Styled.VideoImage
            src="/assets/images/credit-usage-section.svg"
            alt="Credit Usage Guide"
          />
          <Box sx={SX.videoContent}>
            <Box sx={SX.videoTextWrapper}>
              <Typography
                variant="body3"
                fontWeight={700}
                color="common.white"
                noWrap
              >
                Understanding Credit Usage
              </Typography>
              <Typography
                variant="body4"
                fontWeight={500}
                color="grey.400"
                noWrap
                sx={{ letterSpacing: '-0.2px' }}
              >
                Learn how usage is calculated in just 60 seconds
              </Typography>
            </Box>
            <Button
              size="small"
              color="primary"
              variant="contained"
              startIcon={<Icon name="play-video" size={16} />}
              onClick={() => setVideoOpen(true)}
              disableElevation
            >
              Watch the Video
            </Button>
          </Box>
        </Box>

        <Modal open={videoOpen} onClose={() => setVideoOpen(false)}>
          <Box sx={SX.modalBox}>
            <video controls style={{ width: '100%' }} autoPlay>
              <source
                src="https://ai-ark.com/wp-content/uploads/2024/05/Demo-page-AI-ARK-Final.mp4"
                type="video/mp4"
              />
              Your browser does not support the video tag.
            </video>
          </Box>
        </Modal>
      </Box>

      <Box sx={SX.rightColumn}>
        <Box sx={SX.rightTitleSection}>
          <Box sx={SX.rightTitleInner}>
            <Typography
              variant="h5"
              fontWeight={700}
              color="common.white"
              noWrap
            >
              Estimate Your Credit Usage
            </Typography>
            <Typography
              variant="body4"
              fontWeight={500}
              color="grey.400"
              noWrap
              sx={SX.rightSubtitle}
            >
              Get a clear estimate of the credits you&apos;ll need each month
              based on your usage.
            </Typography>
          </Box>
        </Box>

        <Box sx={SX.creditsContent}>
          <Box sx={SX.estimateHeader}>
            <Typography
              variant="body2"
              fontWeight={500}
              color="common.white"
              noWrap
            >
              Estimate Monthly Credits
            </Typography>
            <Button
              color="primary"
              size="small"
              variant="contained"
              startIcon={<Icon name="calculator" size={16} />}
              disableElevation
            >
              Open Credit Calculator
            </Button>
          </Box>

          <Box sx={SX.categoriesGrid}>
            {categories.map((category) => (
              <Box key={category.title} sx={SX.categoryCard}>
                <Box sx={SX.categoryHeader}>
                  <Icon name={category.icon} size={18} color="grey.400" />
                  <Typography
                    variant="body4"
                    fontWeight={500}
                    color="grey.400"
                    noWrap
                  >
                    {category.title}
                  </Typography>
                </Box>
                <Box sx={SX.categoryDivider} />
                <Box>
                  {category.rows.map((row) => {
                    const isFree = row.credit === 0;

                    return (
                      <Box key={row.type} sx={SX.creditRow}>
                        <Box sx={SX.creditRowLeft}>
                          <Icon name="info" size={18} color="grey.400" />
                          <Typography
                            variant="body4"
                            fontWeight={500}
                            color="common.white"
                            noWrap
                          >
                            {row.title}
                          </Typography>
                        </Box>
                        {isFree ? (
                          <Box sx={SX.creditRowRightFree}>
                            <Typography
                              variant="body4"
                              fontWeight={500}
                              color="common.white"
                              noWrap
                            >
                              Free
                            </Typography>
                          </Box>
                        ) : (
                          <Box sx={SX.creditRowRight}>
                            <Icon name="coin" size={18} color="grey.400" />
                            <Typography
                              variant="body4"
                              fontWeight={500}
                              color="common.white"
                              noWrap
                            >
                              x {row.credit}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Styled.Wrapper>
  );
});

CreditUsageOverview.displayName = 'CreditUsageOverview';
